import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';

import { Input } from '../../../components/ui/Input.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { productsApi } from '../../../api/products.js';

/**
 * Controlled product picker for the knowledge base. Links a document (e.g. a
 * "scheda prodotto" datasheet) to an existing product by searching SKU or
 * title. The server turns the selected id into the document's product
 * sourceRef so the AI can fetch the datasheet for that product.
 */
export function ProductLinkPicker({ open, productId, onChange }) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(id);
  }, [term]);

  // Display the currently linked product (name + SKU) when one is set.
  const linkedQuery = useQuery({
    queryKey: ['products', 'one', productId],
    enabled: open && !!productId,
    queryFn: () => productsApi.get(productId)
  });
  const linked = linkedQuery.data || null;

  // Search both the text index (title/brand) and the SKU filter, merge unique.
  const searchQuery = useQuery({
    queryKey: ['products', 'rag-link-search', debounced],
    enabled: open && !productId && debounced.length >= 2,
    queryFn: async () => {
      const [byText, bySku] = await Promise.all([
        productsApi.list({ q: debounced, limit: 8 }),
        productsApi.list({ sku: debounced, limit: 8 })
      ]);
      const seen = new Set();
      const items = [];
      for (const p of [...(byText.items || []), ...(bySku.items || [])]) {
        if (seen.has(String(p._id))) continue;
        seen.add(String(p._id));
        items.push(p);
      }
      return { items };
    }
  });

  const candidates = useMemo(() => (searchQuery.data && searchQuery.data.items) || [], [searchQuery.data]);

  return (
    <div className="rounded-md border border-border bg-surface-subtle p-3">
      <p className="mb-2 text-sm font-medium text-ink">Prodotto collegato</p>
      <p className="mb-3 text-xs text-ink-muted">
        Collega questa scheda a un prodotto esistente: l&apos;assistente la userà quando considera quel prodotto.
      </p>

      {productId ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-elevated px-3 py-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-ink">{linked ? linked.name : 'Prodotto collegato'}</p>
            <p className="truncate text-xs text-ink-muted">{linked ? `${linked.sku}${linked.brand ? ` · ${linked.brand}` : ''}` : productId}</p>
          </div>
          <Button type="button" size="sm" variant="ghost" onClick={() => onChange('')}>
            <X className="mr-1 h-3.5 w-3.5" /> Rimuovi
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Input label="Cerca prodotto" placeholder="SKU o titolo" value={term} onChange={(e) => setTerm(e.target.value)} />
          <div className="max-h-60 overflow-y-auto rounded-md border border-border">
            {debounced.length < 2 ? (
              <p className="flex items-center gap-2 p-3 text-sm text-ink-faint"><Search className="h-4 w-4" /> Digita almeno 2 caratteri.</p>
            ) : searchQuery.isLoading ? (
              <p className="p-3 text-sm text-ink-muted">Ricerca in corso...</p>
            ) : candidates.length === 0 ? (
              <p className="p-3 text-sm text-ink-muted">Nessun prodotto trovato.</p>
            ) : (
              <ul className="divide-y divide-border">
                {candidates.map((p) => (
                  <li key={p._id}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-3 p-2.5 text-left hover:bg-surface-subtle"
                      onClick={() => onChange(String(p._id))}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-ink">{p.name}</span>
                        <span className="block truncate text-xs text-ink-muted">{p.sku}{p.brand ? ` · ${p.brand}` : ''}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
